import { useEffect, useMemo, useState } from 'react';
import APIList from '../http/ApiList';
import { Button, Form, Input, message, Select, Table } from 'antd';
import { isEqual } from 'lodash';

const useGanttFilter = (ganttRef: any, optimizationId: string) => {
  const loadFilterConfig = (optimizationId: string) => {
    if (!optimizationId) return;
    APIList.getGanttConfig()
      .post({
        optimizationId,
      })
      .then((result) => {
        setFilterConfig(result.ganttConfig);
        const ganttConfig = JSON.parse(localStorage.getItem('ganttConfig'));
        if (!ganttConfig) return;
        const config = result.ganttConfig.find((gc) => gc.ganttConfigName === ganttConfig.ganttConfigName);
        if (!config) return;
        applyFilterConfig(config);
      });
  };

  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [filterConfig, setFilterConfig] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const isFilterUsed = useMemo(() => !!filterStatus.length, [filterStatus]);
  const [form] = Form.useForm();
  useEffect(() => {
    setFilterStatus([]);
    loadFilterConfig(optimizationId);
  }, [optimizationId]);

  const openLoadFilterModal = () => {
    setLoadModalVisible(true);
  };
  const openSaveFilterModal = () => {
    form.resetFields(); // 重置表单
    setSaveModalVisible(true);
  };

  // 保存过滤器配置
  const handleSaveFilterConfig = async () => {
    try {
      // 验证表单
      await form.validateFields();
      const { configName, useType } = form.getFieldsValue();

      // 如果还没设置滤器，则提醒用户
      if (!filterStatus.length) {
        message.error('You have not got any filter settings yet.');
        return;
      }

      // 准备要保存的配置数据
      const configData = {
        columnConfig: JSON.stringify(filterStatus),
        ganttConfigName: configName,
        useType,
      };
      APIList.setGanttConfig()
        .post({
          optimizationId,
          isDelete: false,
          ...configData,
        })
        .then((result) => {
          message.success('Update Gantt Configuration Success.');
          loadFilterConfig(optimizationId);
          localStorage.setItem('ganttConfig', JSON.stringify(configData));
          setSaveModalVisible(false);
        });
    } catch (error) {
      console.error('save failed:', error);
    }
  };
  // Gantt Filter Logic
  const applyFilterConfig = (configObj: any) => {
    const { columnConfig, ganttConfigName, useType } = configObj;
    // 对应旧版滤器数据的逻辑
    try {
      const config = JSON.parse(columnConfig);
      // 对应旧版滤器数据的逻辑
      // 提示用户选择使用的旧版滤器会被删除，无法使用，并添加新滤器配置
      if (!Array.isArray(config)) {
        message.error(`Sorry. We have updated the filter's structure and will remove the old version. Please create a new one.`, 7);
        APIList.setGanttConfig()
          .post({
            columnConfig: JSON.stringify(config),
            useType: useType,
            ganttConfigName: ganttConfigName,
            isDelete: true,
            optimizationId: optimizationId,
          })
          .then((result) => {
            loadFilterConfig(optimizationId);
            handleRemoveFilter();
            message.success('Delete Configuration Success.', 2);
          });
        return;
      }

      setFilterStatus(config);
      setLoadModalVisible(false);
      localStorage.setItem('ganttConfig', JSON.stringify(configObj));
      message.success('Apply Gantt Configuration Success.');
    } catch (error) {
      console.error('Failed to parse filter config:', error);
      message.error('Apply Gantt Configuration Failed.');
    }
  };

  const handleRemoveFilter = () => {
    setFilterStatus([]);
    localStorage.removeItem('ganttConfig');
    setLoadModalVisible(false);
  };

  const filterConfigColumns = [
    {
      title: 'Configuration Name',
      dataIndex: 'ganttConfigName',
      key: 'ganttConfigName',
      width: 200,
    },
    {
      title: 'Use Type',
      dataIndex: 'useType',
      key: 'useType',
      width: 100,
      render: (useType) => <span>{useType === '2' ? 'Public' : 'Private'}</span>,
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 120,
    },
    {
      title: '',
      key: '',
      width: 100,
      render: (_, record) => (
        <span>
          <Button
            type="link"
            size="small"
            onClick={() => {
              applyFilterConfig(record);
            }}
          >
            Apply
          </Button>
          {record.isOwner && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                APIList.setGanttConfig()
                  .post({
                    columnConfig: record.columnConfig,
                    useType: record.useType,
                    ganttConfigName: record.ganttConfigName,
                    isDelete: true,
                    optimizationId: optimizationId,
                  })
                  .then((result) => {
                    loadFilterConfig(optimizationId);
                    handleRemoveFilter();
                    message.success('Delete Configuration Success.');
                  });
              }}
            >
              Delete
            </Button>
          )}
        </span>
      ),
    },
  ];
  // 应用过滤器函数
  const applyFilter = () => {
    const gantt = ganttRef.current.ganttInstance();
    const taskStore = gantt.project?.taskStore;
    if (!gantt && !taskStore) return;

    // 应用 / 清空
    if (filterStatus.length === 0) {
      taskStore.clearFilters();
    } else {
      taskStore.filter({
        filters: filterStatus,
        replace: true,
      });
    }
  };

  // 切换 tab 的时候，需要同步多个 gantt 的滤器值
  useEffect(() => {
    if (!ganttRef.current) return;
    applyFilter();
  }, [ganttRef.current, filterStatus]);

  // 更新滤器，需要同步到 State 中
  useEffect(() => {
    if (!ganttRef.current) return;
    const taskStore = ganttRef.current.ganttInstance()?.project?.taskStore;
    if (!taskStore) return;
    const filterCallback = ({ filters }) => {
      // 获取所有过滤器的值
      const groupedFilters = filters.allValues.map(({ id, value, operator, property }) => ({ id, value, operator, property }));
      setFilterStatus((prev) => {
        // 滤器变化前后的值进行对比， 判断是否需要同步 filterStatus 状态的值， 有下面几种情况：
        // - 新旧滤器数量不一致
        // - 新旧滤器的属性不一致， 或者位置变化
        // - 新旧滤器的操作符 operator 不一致
        // - 新旧滤器的值 value 不一致
        if (groupedFilters.length !== prev.length) return groupedFilters;
        const hasDiff = groupedFilters.some((c, i) => {
          const pr = prev[i];
          const isIdDiff = !Object.is(c.id, pr.id);
          const isOpDiff = !Object.is(c.operator, pr.operator);
          const isValueDiff = !isEqual(c.value, pr.value);
          return isIdDiff || isOpDiff || isValueDiff;
        });
        return hasDiff ? groupedFilters : prev;
      });
    };
    taskStore.on('filter', filterCallback);
    return () => {
      taskStore.un('filter', filterCallback);
    };
  }, [ganttRef.current]);

  const SaveFilterModalContent = () => (
    <Form form={form} layout="vertical">
      <Form.Item
        rules={[
          { required: true, message: 'Name Required' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              // 检查配置名是否已存在
              const existingConfig = filterConfig.find((config) => config.ganttConfigName === value);
              if (existingConfig && !existingConfig.isOwner) {
                // 如果配置已存在且不是当前用户的，提示错误
                return Promise.reject(new Error('The name already exists. Please use a different name.'));
              }
              return Promise.resolve();
            },
          },
        ]}
        label="Configuration Name"
        name="configName"
      >
        <Input
          onChange={(e) => {
            const configName = e.target.value;
            // 检查配置名是否已存在
            const existingConfig = filterConfig.find((config) => config.ganttConfigName === configName);
            // 在 filterConfig 中查找匹配的配置
            if (existingConfig) {
              // 如果找到匹配的配置，设置 useType 为配置中的值
              form.setFieldValue('useType', existingConfig.useType || '1');
            } else {
              // 如果没有找到匹配的配置，清空 useType
              form.setFieldValue('useType', undefined);
            }
          }}
        />
      </Form.Item>
      <Form.Item label="Use Type" name="useType" rules={[{ required: true, message: 'Use Type Required.' }]}>
        <Select
          options={[
            {
              value: '1',
              label: 'Private',
            },
            {
              value: '2',
              label: 'Public',
            },
          ]}
        />
      </Form.Item>
    </Form>
  );

  const LoadFilterModalContent = () => (
    <div style={{ position: 'relative' }}>
      <Table
        columns={filterConfigColumns}
        dataSource={filterConfig}
        rowKey={(record, index) => index.toString()}
        pagination={{ pageSize: 10 }}
        size="small"
      />
      {
        // 如果已经设置了滤器，则显示按钮，否则按钮不显示
        isFilterUsed && (
          <Button type="primary" style={{ position: 'absolute', bottom: 10 }} onClick={handleRemoveFilter}>
            Clear Applying Filter
          </Button>
        )
      }
    </div>
  );

  return {
    filterConfig,
    setFilterConfig,
    loadFilterConfig,
    SaveFilterModalContent,
    LoadFilterModalContent,
    openLoadFilterModal,
    openSaveFilterModal,
    saveModalVisible,
    setSaveModalVisible,
    loadModalVisible,
    setLoadModalVisible,
    handleSaveFilterConfig,
  };
};

export default useGanttFilter;
